import React, { useState } from "react";
import { api } from "@/skate/lib/api";
import { useAuth } from "@/skate/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";

const StanceBtn = ({ active, onClick, label, sub, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`flex-1 border p-5 text-left transition-all ${active ? "border-[#FACC15] bg-[#FACC15]/5" : "border-zinc-800 hover:border-zinc-600"}`}
  >
    <div className="font-display text-3xl">{label}</div>
    <div className="font-mono text-[10px] tracking-widest text-zinc-500 mt-1">{sub}</div>
    {active && <Check className="w-4 h-4 text-[#FACC15] mt-3" />}
  </button>
);

const SKILLS = [
  { v: "beginner",     l: "ROOKIE",  d: "Just dropped in" },
  { v: "intermediate", l: "LOCAL",   d: "Got a few tricks" },
  { v: "advanced",     l: "RIPPER",  d: "Hammer maker" },
  { v: "pro",          l: "PRO",     d: "Sponsored / battle-tested" },
];

const STEP_LABELS = ["TURF", "STANCE", "LEVEL", "ARSENAL"];

export const Onboarding = () => {
  const { setUser, user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    bio: "",
    city: "",
    stance: "",
    skill_level: "",
    favorite_tricks: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateStep = () => {
    if (step === 0 && !form.city.trim()) return "Where do you skate?";
    if (step === 1 && !form.stance) return "Pick a stance";
    if (step === 2 && !form.skill_level) return "Pick a level";
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    if (step < STEP_LABELS.length - 1) setStep(step + 1);
    else submit();
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        bio: form.bio.trim(),
        city: form.city.trim(),
        stance: form.stance,
        skill_level: form.skill_level,
        favorite_tricks: form.favorite_tricks
          .split(",").map((t) => t.trim()).filter(Boolean).slice(0, 6),
      };
      const { data } = await api.patch("/users/me/onboarding", payload);
      setUser(data);
      toast.success("Welcome to the league.");
      navigate("/skate/dashboard", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white relative noise">
      <div className="max-w-2xl mx-auto px-5 py-10 relative z-10">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-500">SETUP / STEP {step + 1} OF {STEP_LABELS.length}</span>
            <span className="font-mono text-[10px] tracking-[0.3em] text-[#FACC15]">{STEP_LABELS[step]}</span>
          </div>
          <div className="grid grid-cols-4 gap-1">
            {STEP_LABELS.map((_, i) => (
              <div key={i} className={`h-1 ${i <= step ? "bg-[#FACC15]" : "bg-zinc-800"}`} />
            ))}
          </div>
        </div>

        {user?.username && (
          <div className="mb-6 font-mono text-[10px] tracking-widest text-zinc-500">
            SIGNED IN AS <span className="text-[#FACC15]">@{user.username}</span>
          </div>
        )}

        <div key={step} className="fade-up">
          {step === 0 && (
            <div>
              <h2 className="font-display text-5xl leading-none">WHERE<br/>DO YOU<br/><span className="text-[#FACC15]">SKATE?</span></h2>
              <p className="text-zinc-400 mt-4 text-sm">Your home city. We'll match you with local skaters.</p>
              <div className="mt-8">
                <label className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase block mb-2">City</label>
                <Input
                  data-testid="city-input"
                  autoFocus
                  value={form.city}
                  onChange={(e) => update("city", e.target.value)}
                  placeholder="Barcelona"
                  className="bg-[#121212] border-zinc-800 rounded-none h-14 text-lg font-mono focus:border-[#FACC15] focus:ring-0"
                />
                <label className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase block mt-6 mb-2">Bio (Optional)</label>
                <Textarea
                  data-testid="bio-input"
                  value={form.bio}
                  onChange={(e) => update("bio", e.target.value)}
                  placeholder="Curbs > everything."
                  className="bg-[#121212] border-zinc-800 rounded-none font-mono text-sm focus:border-[#FACC15] focus:ring-0"
                  rows={3}
                />
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <h2 className="font-display text-5xl leading-none">WHICH<br/><span className="text-[#FACC15]">FOOT</span><br/>FORWARD?</h2>
              <p className="text-zinc-400 mt-4 text-sm">Pick your stance.</p>
              <div className="flex gap-3 mt-8">
                <StanceBtn active={form.stance === "regular"} onClick={() => update("stance", "regular")} label="REG" sub="LEFT FOOT FORWARD" testid="stance-regular" />
                <StanceBtn active={form.stance === "goofy"}   onClick={() => update("stance", "goofy")}   label="GOOFY" sub="RIGHT FOOT FORWARD" testid="stance-goofy" />
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h2 className="font-display text-5xl leading-none">HOW<br/>GOOD<br/><span className="text-[#FACC15]">REALLY?</span></h2>
              <p className="text-zinc-400 mt-4 text-sm">Pick a starting tier. ELO will sort you out fast.</p>
              <div className="grid grid-cols-2 gap-3 mt-8">
                {SKILLS.map((s) => (
                  <button
                    key={s.v}
                    type="button"
                    data-testid={`skill-${s.v}`}
                    onClick={() => update("skill_level", s.v)}
                    className={`border p-5 text-left transition-all ${form.skill_level === s.v ? "border-[#FACC15] bg-[#FACC15]/5" : "border-zinc-800 hover:border-zinc-600"}`}
                  >
                    <div className="font-display text-2xl">{s.l}</div>
                    <div className="font-mono text-[10px] tracking-widest text-zinc-500 mt-1">{s.d}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 className="font-display text-5xl leading-none">YOUR<br/>GO-TO<br/><span className="text-[#FACC15]">TRICKS.</span></h2>
              <p className="text-zinc-400 mt-4 text-sm">Comma-separated. Max 6. Optional.</p>
              <div className="mt-8">
                <label className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase block mb-2">Tricks</label>
                <Input
                  data-testid="tricks-input"
                  autoFocus
                  value={form.favorite_tricks}
                  onChange={(e) => update("favorite_tricks", e.target.value)}
                  placeholder="kickflip, tre flip, bs tailslide"
                  className="bg-[#121212] border-zinc-800 rounded-none h-14 text-lg font-mono focus:border-[#FACC15] focus:ring-0"
                />
              </div>
            </div>
          )}
        </div>

        <div className="mt-12 flex gap-3">
          {step > 0 && (
            <Button
              variant="ghost"
              onClick={() => setStep(step - 1)}
              className="flex-1 border border-zinc-800 rounded-none h-14 font-display text-lg tracking-widest text-zinc-400 hover:text-white"
              data-testid="onboarding-back"
            >
              BACK
            </Button>
          )}
          <Button
            data-testid="onboarding-next"
            onClick={next}
            disabled={submitting}
            className="flex-[2] bg-[#FACC15] hover:bg-[#FDE047] text-black rounded-none h-14 font-display text-lg tracking-widest disabled:opacity-50"
          >
            {step === STEP_LABELS.length - 1 ? (submitting ? "ENTERING…" : "ENTER LEAGUE") : "NEXT"}
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
